import type { ItemDependencyState, ItemHealth } from '@/common/api/types/dashboard/health';
import {
  type DependencyImpactState,
  getPredecessorImpactState,
  getSuccessorImpactState,
  type PredecessorImpactOptions,
  type SuccessorImpactOptions,
} from '@/features/item-panel/components/tabs/dependencies/lib/dependencyImpact';

type DependencyEdge = ItemDependencyState['edges'][number];

export type EdgeImpactState = DependencyImpactState | 'cannotEvaluate';

const IMPACT_STATE_BY_EDGE_IMPACT: Record<DependencyEdge['impact'], EdgeImpactState> = {
  BLOCKED: 'error',
  AT_RISK: 'warning',
  CANNOT_EVALUATE: 'cannotEvaluate',
  NO_ISSUE: 'none',
};

export const findDependencyEdge = (
  currentItemHealth: ItemHealth | undefined,
  direction: DependencyEdge['direction'],
  relatedItemId: number,
): DependencyEdge | undefined =>
  currentItemHealth?.dependency?.edges.find(
    (candidate) => candidate.direction === direction && candidate.relatedItemId === relatedItemId,
  );

const getEdgeImpactState = (
  currentItemHealth: ItemHealth | undefined,
  direction: DependencyEdge['direction'],
  relatedItemId: number,
): EdgeImpactState => {
  const edge = findDependencyEdge(currentItemHealth, direction, relatedItemId);
  return edge ? IMPACT_STATE_BY_EDGE_IMPACT[edge.impact] : 'none';
};

export const getPredecessorEdgeImpactState = (
  currentItemHealth: ItemHealth | undefined,
  predecessorId: number,
): EdgeImpactState => getEdgeImpactState(currentItemHealth, 'PREDECESSOR', predecessorId);

export const getSuccessorEdgeImpactState = (
  currentItemHealth: ItemHealth | undefined,
  successorId: number,
): EdgeImpactState => getEdgeImpactState(currentItemHealth, 'SUCCESSOR', successorId);

export const resolvePredecessorImpactState = (
  useEdges: boolean,
  options: PredecessorImpactOptions,
): EdgeImpactState =>
  useEdges
    ? getPredecessorEdgeImpactState(options.currentItemHealth, options.row.id)
    : getPredecessorImpactState(options);

export const resolveSuccessorImpactState = (
  useEdges: boolean,
  options: SuccessorImpactOptions & { currentItemHealth: ItemHealth | undefined },
): EdgeImpactState =>
  useEdges
    ? getSuccessorEdgeImpactState(options.currentItemHealth, options.row.id)
    : getSuccessorImpactState(options);
